
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import BaseInput from '@/components/BaseInput.vue'
import BulkScanSwitchButton from '@/components/common/BulkScanSwitchButton.vue'
import ScannedValueInput from '@/components/common/ScannedValueInput.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconInfo from '@/components/svg/IconInfo.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { UPDATE_SCANNED_RESULT } from '@/store/actions'
import { LOCAL_STORAGE_ITEM, QRCODE_BARCODE_TYPE_REGEX } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { promptVideoPermission } from '@/utils/helpers'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { TError } from 'smartbarcode-web-core/src/utils/types/index'
import { Options, mixins } from 'vue-class-component'
import { EBCRType } from 'smartbarcode-web-core/src/utils/enums/mobileEnums'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
@Options({
  components: {
    IconInfo,
    ScanArea,
    BarcodeSymbologiesSelection,
    BaseInput,
    BulkScanSwitchButton,
    ScannedValueInput,
    IconSwitch,
    SelectionCameraPopup,
  },
  name: 'ScanView',
})
export default class ScanView extends mixins(BarcodeReaderMixin) {
  _barcodeDataCapture: BarcodeCapture | undefined
  codeReader = new BrowserMultiFormatReader()
  isReadyToScan = false
  video: HTMLVideoElement | null = null
  listBlob: Blob[] = []

  async defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.isReadyToScan = true
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    await this.handleScanResult((temp.text as string) || '')
    if (!this.isMultipleScan) {
      codeReader.reset()
    }
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    await this.handleScanResult((result[0].rawValue as string) || '')
    if (!this.isMultipleScan) {
      this.vidOff()
    }
  }

  async scanditDataResult(symbology: Barcode) {
    this.isReadyToScan = true
    if (!symbology) return

    await this.handleScanResult(symbology.data || '')
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  async mounted() {
    await this.initCameraSelection()
    await this.setupCameraSetting()
  }

  async setupCameraSetting() {
    try {
      this.isUserAutheticated()
      this.isReadyToScan = false
      switch (this.barcodeReaderType as EBCRType) {
        case EBCRType.SCANDIT:
          await this.initScanditReader()
          break
        case EBCRType.BARCODE_DETECTION:
          await this.initBarcodeReaderPolyFill()
          break
        default:
          await promptVideoPermission()
          await this.initZxingReader()
          break
      }
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  resetScannedValue() {
    this.listBlob = []
    this.valueCode = ''
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  redirectHome() {
    this.$router.replace({
      name: 'home',
      params: {
        project: this.$route.params.project,
      },
    })
  }

  isUserAutheticated() {
    const isRestrictedPage = this.$route.meta.restricted
    if (!this.isAuth && isRestrictedPage) {
      this.redirectHome()
    }
  }

  async handleCompleteScan() {
    if (!this.isMultipleScan || this.valueCode.trim() === '') {
      return
    }
    this.processScannedText(this.valueCode)
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    }
  }

  async snapshot() {
    try {
      const tempCanvas = document.querySelector('#captureCanvas') as HTMLCanvasElement
      const video = document.querySelector('video')
      if (!video || !tempCanvas) return
      const originalWidth = tempCanvas.width
      const originalHeight = tempCanvas.height
      tempCanvas.width = video.videoWidth
      tempCanvas.height = video.videoHeight
      const context = tempCanvas.getContext('2d')
      if (!context) return
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const blob = await new Promise((resolve) => tempCanvas.toBlob(resolve))
      return blob
    } catch (error) {
      console.error('Error during snapshot:', error)
    }
  }

  handleSaveScannedInfo(blob: Blob) {
    this.listBlob.push(blob)
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  async handleScanResult(plainText: string) {
    this.isMultipleScan ? await this.handleMultipleScan(plainText) : await this.handleSingleScan(plainText)
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }
    const blob = await this.snapshot()
    this.isHandleMultipleScan = true
    this.$confirm(this.$t('confirm_scanned_value', { item: plainText }), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
        this.handleSaveScannedInfo(blob as Blob)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  async handleSingleScan(plainText: string) {
    const blob = await this.snapshot()
    this.handleSaveScannedInfo(blob as Blob)
    this.valueCode = plainText
    this.processScannedText(plainText)
  }

  processScannedText(text: string) {
    const valCode = text.trim()
    const regexBarcodeId = QRCODE_BARCODE_TYPE_REGEX
    const urlParts = valCode.split('/')
    const detectedText = valCode || ''
    if (valCode.match(regexBarcodeId)) {
      const barcodeId = urlParts.pop()
      urlParts.pop()
      const projectId = urlParts.pop()
      localStorage.setItem(LOCAL_STORAGE_ITEM.CAPTURE_TIMESTAMP, new Date().toISOString())
      this.$router.push({
        name: 'detail',
        params: { barcodeId, project: projectId },
      })
    } else {
      const projectCode = this.$route?.params?.project.split('@')
      if (projectCode.length > 1) projectCode.pop()
      this.$router.push({
        name: 'detail',
        params: { barcodeId: `projectCode=${projectCode[0]}` },
        query: { externalId: detectedText, isGenerateBarcode: 1 },
      })
    }
  }
}
